"use client";

import Intercom from "@intercom/messenger-js-sdk";
import { useAuth } from "@the-volte/svc-core-sdk";
import { LocalStorageItems } from "@the-volte/svc-core-sdk/lib/helpers";
import { useEffect } from "react";

import { INTERCOM_TOKEN_SESSION_DURATION, intercomAppId } from "@app/constants";
import { decodeBase64Id } from "@utils/base64";

interface IntercomComponentProps {
  generateIntercomToken: (
    accessToken: string,
    accessRefreshToken: string
  ) => Promise<any>;
}

export default function IntercomComponent({
  generateIntercomToken,
}: IntercomComponentProps) {
  const { user, loaded } = useAuth();

  useEffect(() => {
    if (!loaded) return;

    const initializeIntercom = async () => {
      if (!user) {
        initializeAnonymousIntercom();
        return;
      }

      try {
        const accessToken = localStorage.getItem(LocalStorageItems.TOKEN);
        const accessRefreshToken = localStorage.getItem(
          LocalStorageItems.ACCESS_REFRESH_TOKEN
        );

        if (!accessRefreshToken) {
          console.log(
            "No access refresh token found, initializing anonymous Intercom"
          );
          initializeAnonymousIntercom();
          return;
        }

        if (!accessToken) {
          console.log("No access token found, initializing anonymous Intercom");
          initializeAnonymousIntercom();
          return;
        }

        const data = await generateIntercomToken(
          accessToken,
          accessRefreshToken
        );

        if (!data || "error" in data) {
          console.error("Failed to get Intercom token:", data?.error);
          initializeAnonymousIntercom();
          return;
        }

        const createdAtMs = Date.parse(String(user.dateJoined));
        const createdAt = Number.isFinite(createdAtMs)
          ? Math.floor(createdAtMs / 1000)
          : undefined;

        const appId = data.app_id;
        const sessionDuration = INTERCOM_TOKEN_SESSION_DURATION;

        const decodedUserId = user?.id ? decodeBase64Id(user.id) : "";

        Intercom({
          app_id: appId,
          intercom_user_jwt: data.token,
          user_id: decodedUserId,
          name:
            [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
            undefined,
          email: user?.email || undefined,
          ...(createdAt ? { created_at: createdAt } : {}),
          session_duration: sessionDuration,
        });
      } catch (error) {
        console.error(
          "[DEBUG] {IntercomComponent} Failed to initialize authenticated Intercom:",
          error
        );
        initializeAnonymousIntercom();
      }
    };

    const initializeAnonymousIntercom = () => {
      const appId = intercomAppId;

      if (!appId) {
        console.error("Intercom App ID not configured");
        return;
      }

      let anonymousUserId = "";
      try {
        const storedId = window.localStorage.getItem(
          "intercom_anonymous_user_id"
        );
        anonymousUserId = storedId ?? "";
      } catch (error) {
        console.error("Failed to get Intercom anonymous user ID:", error);
      }

      if (!anonymousUserId) {
        anonymousUserId = `anonymous_${Date.now()}_${Math.random()
          .toString(36)
          .slice(2, 11)}`;
        try {
          window.localStorage.setItem(
            "intercom_anonymous_user_id",
            anonymousUserId
          );
        } catch (error) {
          console.error("Failed to set Intercom anonymous user ID:", error);
        }
      }

      Intercom({
        app_id: appId,
        user_id: anonymousUserId,
        session_duration: INTERCOM_TOKEN_SESSION_DURATION,
      });
    };

    initializeIntercom();
  }, [user, loaded]);

  return null;
}
